import React from "react";
import { Stack, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import { useTranslation } from "react-i18next";

// Import delle icone da MUI
import BedIcon from "@mui/icons-material/Bed";
import TvIcon from "@mui/icons-material/Tv";
import WifiIcon from "@mui/icons-material/Wifi";
import LockIcon from "@mui/icons-material/Lock";
import SaunaIcon from "@mui/icons-material/Bathroom";
import BathtubIcon from "@mui/icons-material/Bathtub";
import KitchenIcon from "@mui/icons-material/Kitchen";
import CoffeeMakerIcon from "@mui/icons-material/CoffeeMaker";
import { BathroomTwoTone } from "@mui/icons-material";
import BalconyIcon from "@mui/icons-material/Balcony";

// Funzione per associare le icone ai servizi
const getIconForService = (service, t, isMobile) => {
  const translatedService = t(service); // Traduci il servizio
  const iconSize = isMobile ? "default" : "small"; // Dimensione dell'icona per mobile e non mobile

  if (translatedService.toLowerCase().includes("letto")) {
    return <BedIcon fontSize={iconSize} />;
  }
  switch (translatedService) {
    case t("Frigo"):
      return <KitchenIcon fontSize={iconSize} />;
    case t("Doppio Balcone"):
      return <BalconyIcon fontSize={iconSize} />;
    case t("Balcone"):
      return <BalconyIcon fontSize={iconSize} />;
    case t("Tv"):
      return <TvIcon fontSize={iconSize} />;
    case t("WiFi"):
      return <WifiIcon fontSize={iconSize} />;
    case t("Bollitore elettrico per tisane"):
      return <CoffeeMakerIcon fontSize={iconSize} />;
    case t("Cassaforte di sicurezza"):
      return <LockIcon fontSize={iconSize} />;
    case t("Sauna finlandese"):
      return <SaunaIcon fontSize={iconSize} />;
    case t("Vasca idromassaggio"):
      return <BathtubIcon fontSize={iconSize} />;
    case t("Bagno Privato"):
      return <BathroomTwoTone fontSize={iconSize} />;
    default:
      return null;
  }
};

const CameraDetails = ({ services, isCenter, isRoomDialog }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:768px)");

  return (
    <Stack
      justifyContent={isCenter ? "center" : undefined}
      alignItems={isCenter ? "center" : undefined}
      spacing={isCenter ? 0 : 1}
      width={isMobile || isRoomDialog ? undefined : "100vw"}
    >
      <Stack
        alignItems={
          isCenter ? "center" : isMobile || isRoomDialog ? undefined : "center"
        }
        flexDirection={isMobile ? "column" : isRoomDialog ? "column" : "row"}
        flexWrap={isMobile ? "wrap" : "wrap"} // Permetti wrapping su tutte le dimensioni
        width={isMobile ? undefined : "85%"}
        justifyContent={isMobile ? undefined : "space-around"}
        pt={isMobile || isRoomDialog ? undefined : 4.5}
        sx={{
          "& > *": {
            mt: isMobile ? 1 : 2, // Spazio tra le righe su dispositivi non mobile
          },
        }}
      >
        {services.map((service, index) => (
          <Stack key={index} direction="row" alignItems="center" spacing={1}>
            {/* Riduci la dimensione dell'icona su dispositivi non mobile */}
            {!isCenter || !isMobile
              ? getIconForService(service, t, isMobile)
              : null}
            <Typography
              variant="body1"
              sx={{
                fontSize: isMobile || !isCenter ? undefined : "14px",
                whiteSpace: isMobile ? "normal" : "nowrap", // Non avvolgere su non mobile
              }}
            >
              {t(service)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};



const RoomDetails = ({
  selectedIndex,
  isCenter,
  room,
  isRoomDialog = false,
}) => {
  const { t } = useTranslation();
  const roomMap = {
    room1: 0,
    room2: 1,
    room3: 2,
    room4: 3,
    room5: 4,
  };

  const camere = [
    {
      name: t("Camera 1 - Inno al Re"),
      services: [
        t("Bagno Privato"),
        t("Doppio Balcone"),
        t("WiFi"),
        t("Tv"),
        t("Sauna finlandese"),
        t("Frigo"),
        t("Cassaforte di sicurezza"),
        t("Bollitore elettrico per tisane"),
      ],
    },
    {
      name: t("Camera 2 - Il Barbiere di Siviglia"),
      services: [
        t("Bagno Privato"),
        t("Doppio Balcone"),
        t("WiFi"),
        t("Tv"),
        t("Vasca idromassaggio"),
        t("Frigo"),
        t("Cassaforte di sicurezza"),
        t("Bollitore elettrico per tisane"),
      ],
    },
    {
      name: t("Camera 3 - Osteria di Marechiaro"),
      services: [
        t("Bagno Privato"),
        t("Balcone"),
        t("WiFi"),
        t("Tv"),
        t("Frigo"),
        t("Cassaforte di sicurezza"),
        t("Bollitore elettrico per tisane"),
      ],
    },
    {
      name: t("Camera 4 - La Molinara"),
      services: [
        t("Bagno Privato"),
        t("Balcone"),
        t("WiFi"),
        t("Tv"),
        t("Frigo"),
        t("Cassaforte di sicurezza"),
        t("Bollitore elettrico per tisane"),
      ],
    },
    {
      name: t("Camera 5 - Matrimonio Inaspettato"),
      services: [
        t("Bagno Privato"),
        t("Balcone"),
        t("WiFi"),
        t("Tv"),
        t("Frigo"),
        t("Cassaforte di sicurezza"),
        t("Bollitore elettrico per tisane"),
      ],
    },
  ];

  const selectedCameraIndex = roomMap[room];
  const selectedCamera = camere[selectedCameraIndex];
  const isMobile = useMediaQuery("(max-width:768px)");
  return (
    <Stack
      direction="row"
      spacing={2}
      padding={isCenter ? 1 : 0}
      sx={{
        zIndex: 4,
        width: "100%",
        display: "flex",
        height: "100%",
        maxWidth: "1000px",
        alignItems: isCenter ? "center" : undefined,
        justifyContent: isCenter ? "center" : undefined,
        flexDirection: "column",
      }}
    >
      {selectedCamera ? (
        <CameraDetails
          isCenter={isCenter}
          services={selectedCamera.services}
          isRoomDialog={isRoomDialog}
        />
      ) : (
        <Typography variant="h6" color="error">
          {t("Nessuna camera trovata per l'indice selezionato.")}
        </Typography>
      )}
    </Stack>
  );
};

export default RoomDetails;
